/**
 * Times are stored as `HH:MM`, 24-hour — it is what a list sorts by and what
 * the server keeps. Only what the clinic reads and types is 12-hour, because
 * "5:30 pm" is how an appointment is said out loud, and a field showing 17:30
 * is one more translation to do while somebody is waiting on the phone.
 */

export interface Clock12 {
  hour: number
  minute: number
  meridiem: 'am' | 'pm'
}

/** `"17:30"` into its 12-hour parts, or null when there is nothing to show. */
export function splitClock(wire: string | null | undefined): Clock12 | null {
  if (!wire) return null
  const match = wire.match(/^(\d{1,2}):(\d{2})/)
  if (!match) return null
  const hours = Number(match[1])
  const minute = Number(match[2])
  if (hours > 23 || minute > 59) return null
  return {
    // 00:xx is 12 am and 12:xx is 12 pm — the two the modulo gets wrong on its
    // own, and the two a booking clerk actually types.
    hour: hours % 12 === 0 ? 12 : hours % 12,
    minute,
    meridiem: hours < 12 ? 'am' : 'pm',
  }
}

/** 12-hour parts back to the stored `HH:MM`. */
export function toWireTime(hour: number, minute: number, meridiem: 'am' | 'pm'): string {
  const base = hour % 12
  const hours = meridiem === 'pm' ? base + 12 : base
  return `${String(hours).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

/** `"17:30"` as `"5:30 pm"`. Empty for nothing, never "Invalid". */
export function formatClock(wire: string | null | undefined): string {
  const parts = splitClock(wire)
  if (!parts) return ''
  return `${parts.hour}:${String(parts.minute).padStart(2, '0')} ${parts.meridiem}`
}

/**
 * What someone typed, back to `HH:MM`, or null if it is not a time.
 *
 * Accepts both clocks and most of the ways either gets written: "5:30 pm",
 * "5.30pm", "530 pm", "17:30", "1730", "5 pm". Without a meridiem it is read as
 * 24-hour, so an existing habit of typing 17:30 keeps working.
 */
export function parseTimeInput(text: string): string | null {
  const raw = text.trim().toLowerCase()
  if (!raw) return null

  const meridiemMatch = raw.match(/([ap])\.?m?\.?$/)
  const meridiem = meridiemMatch ? (`${meridiemMatch[1]}m` as 'am' | 'pm') : null
  const digits = (meridiem ? raw.slice(0, meridiemMatch!.index) : raw).replace(/\D/g, '')
  if (!digits) return null

  let hours: number
  let minutes: number
  if (digits.length <= 2) {
    hours = Number(digits)
    minutes = 0
  } else {
    // "930" is 9:30, "1730" is 17:30 — the split is always the last two.
    hours = Number(digits.slice(0, digits.length - 2))
    minutes = Number(digits.slice(-2))
  }

  if (minutes > 59) return null
  if (meridiem) {
    if (hours < 1 || hours > 12) return null
    return toWireTime(hours, minutes, meridiem)
  }
  if (hours > 23) return null
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}
